import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  FormGroupDirective,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import {NGXLogger} from 'ngx-logger';
import {ComponentConfiguration} from '@twpub/core/utils';
import {ComponentFieldModel, ConfigurableComponent, OptionItem} from '@twpub/core/models';
import {ComponentDeclarationService} from '@twpub/core/services';
import {ALL_COMP_CATEGORIES, ComponentCategory, getComponentCategoryName} from '@twpub/core/enums';
import {Subscription} from 'rxjs';

import {MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle} from '@angular/material/expansion';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatOption, MatSelect} from '@angular/material/select';
import {ComponentEditorFieldComponent} from '../component-editor-field/component-editor-field.component';

@Component({
  selector: 'pub-component-editor',
  templateUrl: './component-editor.component.html',
  styleUrls: ['./component-editor.component.scss'],
  standalone: true,
  imports: [MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatSelect, MatOption, ComponentEditorFieldComponent]
})
export class ComponentEditorComponent implements OnInit, OnDestroy {
  @Input() scheme!: string;
  @Input() compCategory!: ComponentCategory | string;
  @Input() compConfig?: ComponentConfiguration<ConfigurableComponent>;

  fieldModels: ComponentFieldModel<any>[] = [];
  formGroup!: UntypedFormGroup;
  compTypeModel?: ComponentFieldModel<any>;
  compTypeModelOptions: OptionItem[] = [];
  private optionsSub?: Subscription;
  private formValuesSub?: Subscription;

  constructor(
    private logger: NGXLogger,
    private compDeclarationService: ComponentDeclarationService,
    private parent: FormGroupDirective
  ) {
  }

  ngOnInit() {
    this.initForm();
    this.setupFormValueListener();
  }

  ngOnDestroy(): void {
    this.unsubscribeOptions();
    if (this.formValuesSub) {
      this.formValuesSub.unsubscribe();
    }
  }

  private initForm() {
    if (this.compConfig) {
      const compModel = this.compDeclarationService.getModel(this.scheme, this.compCategory, this.compConfig);
      this.compTypeModel = compModel.componentFields[0];
      if (Array.isArray(this.compTypeModel.options)) {
        this.compTypeModelOptions = this.compTypeModel.options;
      } else {
        this.logger.error('ComponentEditorComponent.initForm: compTypeModel.options is an OptionProvider, which is not supported in this context.');
        this.compTypeModelOptions = [];
      }
      this.fieldModels = compModel.componentFields.slice(1);
      this.formGroup = this.toFormGroup(compModel.componentFields as ComponentFieldModel<any>[]);
      this.parent.form.addControl(this.compCategory, this.formGroup);
      this.formGroup.get(this.compTypeModel.key)?.setValue(this.compTypeModel.value);
    }
  }

  toFormGroup(compFields: ComponentFieldModel<any>[]) {
    const group: any = {};
    compFields.forEach(field => {
      group[field.key] = new UntypedFormControl(field.key || '', field.validators || []);
    });
    return new UntypedFormGroup(group);
  }

  getCompName = (): string => getComponentCategoryName(this.compCategory);

  changeComponent(compType: string) {
    this.logger.trace('ComponentEditorComponent.changeComponent:', {compType});
    if (this.compConfig) {
      this.compConfig.componentType = compType;
      this.logger.trace('ComponentEditorComponent.changeComponent: setting type', {compType});
      this.unsubscribeFormValues();
      this.initForm();
      this.setupFormValueListener();
    }
  }

  private setupFormValueListener() {
    if (this.formGroup) {
      this.formValuesSub = this.formGroup.valueChanges.subscribe(values => {
        if (this.compConfig) {
          Object.keys(values).forEach(key => {
            this.compConfig!.setConfigValue(key, values[key]);
          });
        }
      });
    }
  }

  trackByFieldKey(index: number, field: ComponentFieldModel<any>): string {
    return field.key;
  }

  private unsubscribeFormValues() {
    if (this.formValuesSub) {
      this.formValuesSub.unsubscribe();
      this.formValuesSub = undefined;
    }
  }

  private unsubscribeOptions() {
    if (this.optionsSub) {
      this.optionsSub.unsubscribe();
      this.optionsSub = undefined;
    }
  }

  protected readonly types = ALL_COMP_CATEGORIES;
}
